import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { trackHoverOnPinWithTime } from '@rsos/components/capstone/common/Map/SpatialLayers/spatialLayersTrackings';
import ICON_MAPPING from '@rsos/components/capstone/common/Map/assets/calls/sprites-output/sprites-info.json';
import callsSprites from '@rsos/components/capstone/common/Map/assets/calls/sprites-output/sprites_2x.png';
import CRGCallsLayer from '@rsos/components/capstone/common/Map/useDeckGL/RSOSLayers/CRGCallsLayer';
import useDeckOverlay from '@rsos/components/capstone/common/Map/useDeckGL/useDeckOverlay';
import { isLocationInPolygon } from '@rsos/components/capstone/common/Map/utils/spatialLayersHelpers';
import { SOURCE_SMS } from '@rsos/constants/locationSources';
import useIsTF911Enabled from '@rsos/hooks/useIsTF911Enabled';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';
import { isCallerIDFromOrg } from '@rsos/utils/isCallerIDFromOrg';
import useIsIRP from '@rsos/utils/useIsIRP';
import callQueueSelectorNext from '../../../selectors/callQueueSelectorNext';
import { trackHoverOnPin } from '../../JurisdictionView/jurisdictionViewTrackings';
import CallMapPopover from './CallMapPopover';

const MARKER_SIZES = {
  sizeScale: 60, // this is the factor to scale the icon.
  sizeMinPixels: 38, // when zoomed out, the max height of the icon.
  sizeMaxPixels: 40, // when zoomed in, the max height of the icon.
};

/**
 * Deck.gl handling for drawing calls in a CRG polygon
 * @param {Object} activeCrgObject
 * @param {Object} activeCrgObject.spatialObject - the current active spatial object
 * @param {Object} activeCrgObject.activeFloor - data for the current active floor
 * @param {Object} activeCrgObject.deeplink - deeplink data for the floors
 * @param {Boolean} activeCrgObject.deeplink.loading - loading state
 * @param {Object} activeCrgObject.deeplink.error
 * @param {Object} activeCrgObject.deeplink.data - data pertaining to the CRG building floors
 * @param {Instance} map - Google Maps instance
 */
const CallsInPolygonGL = ({ activeCrgObject, map }) => {
  const [hoveredCallData, setHoveredCallData] = useState(null);
  const [timer, setTimer] = useState(0);
  const hoveredTimer = useRef(null);

  const overlay = useDeckOverlay('google');
  const isTF911Enabled = useIsTF911Enabled();

  const allCalls = useSelector(state =>
    callQueueSelectorNext(state, isTF911Enabled),
  );

  const { irpVersion } = useIsIRP();

  const callsInPolygon = useMemo(() => {
    return Object.keys(allCalls)
      .filter(call => {
        const { mostRecentLocation, mostRecentSecondaryLocation } =
          getMostRecentLocations(allCalls[call]);
        const location = mostRecentLocation || mostRecentSecondaryLocation;
        const coordinates = [
          parseFloat(location.longitude),
          parseFloat(location.latitude),
        ];
        // If `map_area` is falsy, the spatial object doesn't have phase 2 data
        return (
          activeCrgObject?.activeFloor?.map_area &&
          !!isLocationInPolygon(
            activeCrgObject?.activeFloor?.map_area,
            coordinates,
          )
        );
      })
      .map(phoneNumber => {
        const { mostRecentLocation, mostRecentSecondaryLocation } =
          getMostRecentLocations(allCalls[phoneNumber]);
        const location = mostRecentLocation || mostRecentSecondaryLocation;

        return location;
      });
  }, [allCalls, activeCrgObject]);

  const hoveredCallIsConnectedCar = isCallerIDFromOrg(
    'sxm',
    callsInPolygon,
    hoveredCallData?.caller_id,
  );

  const handleHoverData = useCallback(
    location => {
      if (location) {
        setHoveredCallData(location);
        overlay.setCursorPointer();
        trackHoverOnPin(irpVersion);
      } else {
        setHoveredCallData(null);
        overlay.setCursorDefault();
      }
    },
    [irpVersion, overlay],
  );

  useEffect(() => {
    if (map && overlay) {
      overlay.setMap(map);
    }
  }, [map, overlay]);

  useEffect(() => {
    if (map && overlay && callsInPolygon.length) {
      const data = { primaryLocations: callsInPolygon, secondaryLocations: [] };

      const layerProps = {
        iconAtlas: callsSprites,
        iconMapping: ICON_MAPPING,
        sizeUnits: 'meters',
        visible: true,
        pickable: true,
        highlightColor: [255, 255, 255, 60],

        getIcon: d => {
          if (d.source === SOURCE_SMS) {
            return 'allcall_textto911';
          }
          return 'allcall_marker';
        },
        getPosition: d => {
          return [d?.longitude, d?.latitude];
        },
        ...MARKER_SIZES,
      };
      const callsLayer = new CRGCallsLayer({
        data,
        visible: true,
        autoHighlight: true,
        layerProps,

        onHover: d => {
          /**
           * @constant isSameLocObj - prevent re-computation when cursor moves inside the marker
           */

          const isSameLocObj =
            hoveredCallData &&
            d?.object &&
            hoveredCallData.caller_id === d?.object?.caller_id &&
            hoveredCallData.call_start_time === d?.object?.call_start_time;

          if (!isSameLocObj) {
            handleHoverData(d?.object);
          }
        },
      });

      // Temporary layer intended for this component only.
      // No need to storeLayers.save() or overlay.setLayers(storeLayers.getAll())

      overlay.setLayers([callsLayer]);
    }
  }, [callsInPolygon, handleHoverData, hoveredCallData, map, overlay]);

  useEffect(() => {
    if (hoveredCallData) {
      if (timer < 2) {
        hoveredTimer.current = setTimeout(() => {
          setTimer(timer => timer + 1);
        }, 1000);
      }

      if (timer >= 1 && timer < 2) {
        trackHoverOnPinWithTime(1, hoveredCallIsConnectedCar, irpVersion);
      }
      if (timer >= 2) {
        trackHoverOnPinWithTime(2, hoveredCallIsConnectedCar, irpVersion);
      }
    } else {
      setTimer(0);
    }
    return () => {
      clearTimeout(hoveredTimer.current);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredCallData, timer]);

  return (
    <>
      <CallMapPopover overlay={overlay} data={hoveredCallData} />
    </>
  );
};

export default CallsInPolygonGL;
